import { useCallback, useMemo } from 'react'

import { useLoading, useServerError } from 'shared/hooks'
import {
  useUpdateTagMutation,
  useAssignTagMutation,
  useUnassignTagMutation,
  useCreateTagMutation
} from 'modules/Labels/graphql/mutations'

import { useTags } from 'modulesCommon/Tags/useTags'
import { GetTagsDocument } from 'modulesCommon/Tags/graphql/getTags.types'
import { Tag, TagUpdate, TagCreate } from 'shared/__gql-types'

export const useServiceTags = (labelId: string = '') => {
  const { tags, loading: tagsLoading, error: tagsError } = useTags({ labelId: ''})
  const { tags: zoneTags, loading: zoneTagsLoading, error: zoneTagsError } = useTags({ labelId })

  const tagsHash = useMemo(() => tags.reduce((res: {[id: string]: Tag}, tag: Tag) => ({ ...res, [tag.id]: tag }), {}), [tags])

  const [createTag, { loading: tagCreateLoading, error: tagCreateError }] = useCreateTagMutation({
    update(cache, { data }, { variables }) {
      if (typeof data?.createTag !== 'undefined') {
        const res = cache.readQuery<{ tags: Tag[] }>({
          query: GetTagsDocument,
          variables: { filter: { labelId: ''} }
        })

        cache.writeQuery({
          query: GetTagsDocument,
          variables: { filter: { labelId: ''} },
          data: {
            tags: [...res?.tags ?? [], data.createTag]
          }
        })
      }
    }
  })
  const [updateTag, { loading: isTagUpdateLoading, error: updateTagError }] = useUpdateTagMutation({
    update(cache, { data }, { variables }) {
      if (typeof data?.updateTag !== 'undefined' && variables?.input) {
        const res = cache.readQuery<{ tags: Tag[] }>({
          query: GetTagsDocument,
          variables: { filter: {} }
        })

        cache.writeQuery({
          query: GetTagsDocument,
          variables: { filter: {} },
          data: {
            tags: (res?.tags ?? []).map((f) => f.id === variables.input?.id ? {...f, ...variables.input} : f)
          }
        })
      }
    }
  })

  const [assignTag, { loading: tagAssignLoading, error: assignTagError }] = useAssignTagMutation({
    update(cache, { data }, { variables }) {
      if (typeof data?.assignTag !== 'undefined' && variables?.input) {
        const res = cache.readQuery<{ tags: Tag[] }>({
          query: GetTagsDocument,
          variables: { filter: { labelId } }
        })

        cache.writeQuery({
          query: GetTagsDocument,
          variables: { filter: { labelId } },
          data: {
            tags: [...res?.tags ?? [], tagsHash[variables.input.tagId ?? '']]
          }
        })
      }
    }
  })
  const [unassignTag, { loading: tagUnassignLoading, error: unassignTagError }] = useUnassignTagMutation({
    update(cache, { data }, { variables }) {
      if (typeof data?.unassignTag !== 'undefined' && variables?.input) {
        const res = cache.readQuery<{ tags: Tag[] }>({
          query: GetTagsDocument,
          variables: { filter: { labelId } }
        })

        cache.writeQuery({
          query: GetTagsDocument,
          variables: { filter: { labelId } },
          data: {
            tags: (res?.tags ?? []).filter((f) => f.id !== variables.input?.tagId)
          }
        })
      }
    }
  })

  const labelTagsIds = useMemo(() => zoneTags.map((tag) => tag.id ?? ''), [zoneTags])

  const onCreateTag = useCallback(
    (values: TagCreate) => createTag({ variables: { input: { ...values, label_id: labelId } } }),
    [createTag, labelId]
  )

  const onToggleTag = useCallback(
    (tag: Tag, active: boolean) => {
      const tagAction = active ? unassignTag : assignTag

      return tagAction({
        variables: {
          input: {
            tagId: tag.id,
            labelId
          }
        }
      })
    },
    [unassignTag, assignTag, labelId]
  )

  const onUpdateTag = useCallback((values: TagUpdate) => updateTag({ variables: { input: values } }), [updateTag])

  useLoading({
    isLoading:
      tagsLoading ||
      tagCreateLoading ||
      zoneTagsLoading ||
      isTagUpdateLoading ||
      tagAssignLoading ||
      tagUnassignLoading
  })

  useServerError([
    tagsError,
    tagCreateError,
    updateTagError,
    zoneTagsError,
    assignTagError,
    unassignTagError
  ])

  return {
    tags,
    labelTagsIds,
    onCreateTag,
    onUpdateTag,
    onToggleTag,
  }
}
